import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
/* eslint-disable react/jsx-no-useless-fragment */
import { Children, isValidElement, useMemo } from 'react';
function isTruthy(value) {
    return Boolean(value);
}
function childIsFunction(child) {
    return typeof child === 'function' && child.length > 0;
}
/**
 * Concept borrowed from Solid: https://www.solidjs.com/docs/latest/api#show
 *
 * Using <Show> can be an improvement over ternary or boolean expressions in some cases.
 *
 * @example
 * ```tsx
 * <Show when={!loading} fallback={<Spinner />}>
 *    <div>All done</div>
 * </Show>
 * ```
 *
 * @example
 * ```tsx
 * <Show when={data}>
 *   {({ transaction }) => <div>{transaction.address}</div>}
 * </Show>
 * ```
 */
export function Show({ when, fallback, children }) {
    if (isTruthy(when)) {
        if (childIsFunction(children)) {
            return _jsx(_Fragment, { children: children(when) });
        }
        return _jsx(_Fragment, { children: children });
    }
    if (fallback) {
        return _jsx(_Fragment, { children: fallback });
    }
    return null;
}
/**
 * Only renders when it is the first `<Match>` in a `<Switch>`
 * whose `when` prop is truthy.
 *
 * Borrowed from Solid: https://www.solidjs.com/docs/latest/api#switchmatch
 */
export function Match({ when, children }) {
    if (!when) {
        // This block should never execute based on the implementation of <Switch>
        return null;
    }
    if (isTruthy(when) && childIsFunction(children)) {
        return _jsx(_Fragment, { children: children(when) });
    }
    if (!childIsFunction(children)) {
        return _jsx(_Fragment, { children: children });
    }
    return null;
}
/**
 * Renders the first `<Match>` whose `when` prop is truthy.
 *
 * Borrowed from Solid: https://www.solidjs.com/docs/latest/api#switchmatch
 *
 * @example
 * ```tsx
 * <Switch fallback={<p>A fallback</p>}>
 *    <Match when={false}>
 *     <p>First match</p>
 *   </Match>
 *   <Match when={true}>
 *    <p>Second match</p>
 *   </Match>
 *   <Match when={data}>
 *    {({ transaction }) => <p>{transaction.address}</p>}
 *   </Match>
 * </Switch>
 * ```
 */
export function Switch({ fallback, children }) {
    const match = useMemo(() => Children.toArray(children).find((child) => isValidElement(child) && // First React Element
        child.type === Match && // That is a <Match>
        child.props.when), [children]);
    if (match) {
        return _jsx(_Fragment, { children: match });
    }
    if (fallback) {
        return _jsx(_Fragment, { children: fallback });
    }
    return null;
}
